import React from 'react'
import { useTranslation } from 'react-i18next'
import Head, { PageHead } from '@fs/zion-head'
import { isTranslationAvailable } from '@fs/zion-locale'
import { useLocation } from '@fs/zion-router'
import { rootstechSocialShare } from '../../images'

// Helper: Detect image MIME type from URL
function getImageType(imageUrl = '') {
  if (imageUrl.match(/\.png/gi)) return 'image/png'
  if (imageUrl.match(/\.gif/gi)) return 'image/gif'
  if (imageUrl.match(/\.svg/gi)) return 'image/svg+xml'
  return 'image/jpeg'
}

// Helper: Build SEO data from Brightspot data object with fallback hierarchy
function buildSeoData(data) {
  if (!data) return null

  return {
    ...data.seo,
    title: data?.seo?.title || data?.title || data?.name,
    description: data?.seo?.description || data?.description,
    openGraph: {
      ...data?.seo?.openGraph,
      title: data?.seo?.openGraph?.title || data?.seo?.title || data?.title || data?.name,
      description: data?.seo?.openGraph?.description || data?.seo?.description || data?.description,
    },
  }
}

// Helper: Get page title from pathname
function getPageTitle(pathname, t) {
  // NOTE: This does not handle all page titles. In some cases, the title needs specific data we don't have here.
  // SeriesPage, SessionPage, BoothPage, and SpeakerPage set their own title.
  const titleMap = {
    '/': isTranslationAvailable('rootstech.home.page-title')
      ? t('rootstech.home.page-title', 'RootsTech Genealogy Conference & Learning Library')
      : t('navigation.home.label', 'Home'),
    '/search': t('navigation.on-demand.label', 'On Demand'),
    '/library': t('library.header', 'Video Library'),
    '/schedule/': t('navigation.schedule.label', 'Schedule'),
    '/playlist': t('navigation.playlists.label', 'My Playlists'),
    '/playlists/': t('navigation.playlists.label', 'My Playlists'),
    '/history': t('navigation.watch-history.label', 'Watch History'),
    '/dashboard/': t('navigation.dashboard.label', 'Dashboard'),
    '/dashboard/access': t('navigation.dashboard-access.label', 'Dashboard Access'),
    '/dashboard/authorization': t('navigation.dashboard-authorization.label', 'Dashboard Authorization'),
    '/user': t('navigation.user.label', 'User'),
    '/faq': t('navigation.faq.label', 'Frequently Asked Questions'),
    '/about': t('about.banner.heading', 'About Us'),
    '/subscription': t('conference-announce.subscribe.for.updates', 'Subscribe for Updates'),
    '/getting-started': t('search.getting-started.label', 'Getting Started'),
    '/registration/': t('event.reg.attend', 'Register for RootsTech'),
    '/registration/in-person/': t('event.reg.attend.in-person.register', 'In-Person Event Registration'),
    '/registration/online/': t('event.reg.attend.virtual.title', 'Online Event Registration'),
    '/registration/in-person/expohall/': t('event.reg.attend.expohall.register', 'Expo Hall Only Registration'),
    '/registration/in-person/discovery-day/': t(
      'event.reg.attend.discovery-day.register',
      'Discovery Day Registration'
    ),
    '/registration/online/arc/': t(
      'event.reg.attend.arc.register',
      'Archivist and Record Custodian Symposium Online Registration'
    ),
  }

  return titleMap[pathname] ?? t('site-title', 'RootsTech')
}

const SeoPageHead = ({ seo, data }) => {
  const [t] = useTranslation()
  const { pathname } = useLocation()

  // Build SEO data from data prop or use seo prop directly
  const seoData = data ? buildSeoData(data) : seo

  if (!seoData) {
    return null
  }

  // Extract SEO data
  const { twitter, canonicalLink, title, description, openGraph } = seoData
  const clientTitle = getPageTitle(pathname, t)
  const seoTitle = title || clientTitle
  const seoDescription = description || ''
  const twitterUsername = twitter?.username !== '@FamilySearch' ? twitter?.username : '@RootsTechConf'
  const twitterImage = twitter?.image || {}
  const openGraphTitle = openGraph?.title || seoTitle
  const openGraphDescription = openGraph?.description || seoDescription
  const openGraphImage = openGraph?.image || {}
  const openGraphImageMetadata = openGraphImage?.metadata || {}
  const image = openGraphImage?.url || rootstechSocialShare

  return (
    <>
      <PageHead
        title={seoTitle}
        description={seoDescription}
        socialShareTitle={openGraphTitle}
        socialShareDescription={openGraphDescription}
        siteName={t('global.rootstech', 'RootsTech')}
        image={{
          alt: openGraphTitle,
          url: image,
          width: openGraphImageMetadata?.origWidthPixels || 1200, // 1200
          height: openGraphImageMetadata?.origHeightPixels || 630, // 630
        }}
        twitterSite={twitterUsername}
        twitterCreator={twitterUsername}
        twitterCard={twitterImage?.url ? 'summary_large_image' : 'summary'}
        omitSiteName
      />
      <Head>
        {seoData?.noIndexRobot && <meta name="robots" content="noindex" />}
        {seoData?.noFollowRobot && <meta name="robots" content="nofollow" />}
        {seoData?.noImageIndexRobot && <meta name="robots" content="noimageindex" />}
        {openGraphImage?.url && <meta property="og:image:type" content={getImageType(openGraphImage.url)} />}
        <link rel="canonical" href={canonicalLink || window?.location?.href} />
      </Head>
    </>
  )
}

export default SeoPageHead
